/**
 * Domínio: Log de Eventos (M10) — GUPM-139
 * -----------------------------------------
 * Feed operacional legível ("MSE-03 atingiu nível ALERTA") — responde
 * "o que aconteceu na barragem", distinto do audit_log ("quem mexeu no quê").
 *
 * Fonte = compute-on-read: eventos são DERIVADOS de leituras + trocas de
 * plantão (zero tabela nova, não toca no cron de ingestão). Estas funções
 * são puras (sem React/Supabase/DOM); o datasource só busca e chama.
 *
 * Escopo POC: transição de nível de leitura + trocas de plantão. ANOMALIA e
 * SIRENE dependem de M4/M5 (placeholders) — ficam fora por ora.
 */

export type EventType = 'ALERTA' | 'ATENCAO' | 'ANOMALIA' | 'SIRENE' | 'SISTEMA' | 'EMERGENCIA';

export interface LogEvent {
  id: string;
  ts: Date;
  type: EventType;
  msg: string;
  /** Código do instrumento relacionado (tag), quando houver. */
  inst?: string | null;
  /** Código/ID do TARP relacionado (tag), quando houver. */
  tarp?: string | null;
}

/** Pills de filtro na ordem do protótipo v5.4.4. */
export const EVENT_FILTERS = ['TODOS', 'ALERTA', 'ATENCAO', 'ANOMALIA', 'SIRENE', 'SISTEMA'] as const;
export type EventFilter = (typeof EVENT_FILTERS)[number];

/** Rótulo pt-BR por tipo/filtro (acentua ATENÇÃO/EMERGÊNCIA). */
export const EVENT_LABEL: Record<string, string> = {
  TODOS: 'TODOS',
  ALERTA: 'ALERTA',
  ATENCAO: 'ATENÇÃO',
  ANOMALIA: 'ANOMALIA',
  SIRENE: 'SIRENE',
  SISTEMA: 'SISTEMA',
  EMERGENCIA: 'EMERGÊNCIA',
};

type ReadingLevel = 'NORMAL' | 'ATENCAO' | 'ALERTA' | 'EMERGENCIA' | null;

export interface ReadingLike {
  id: string;
  ts: Date;
  level: ReadingLevel;
  value: number;
  instrumentCode: string;
}

/**
 * Deriva eventos das leituras emitindo um evento apenas na **transição** para
 * um nível não-normal (ATENÇÃO/ALERTA/EMERGÊNCIA) — não uma leitura por linha.
 * Agrupa por instrumento, percorre em ordem cronológica e compara com o nível
 * anterior: só gera evento quando o nível muda e o novo não é NORMAL. Isso
 * evita inundar o feed quando um instrumento permanece em atenção por N leituras.
 *
 * Aproximação POC: o nível anterior à janela carregada é assumido NORMAL — se a
 * janela começa no meio de um período de atenção, a 1ª leitura conta como
 * transição. Aceitável enquanto a fonte é compute-on-read (sem tabela de estado).
 */
export function buildReadingEvents(readings: ReadingLike[]): LogEvent[] {
  const byInstrument = new Map<string, ReadingLike[]>();
  for (const r of readings) {
    const arr = byInstrument.get(r.instrumentCode);
    if (arr) arr.push(r);
    else byInstrument.set(r.instrumentCode, [r]);
  }

  const events: LogEvent[] = [];
  for (const [code, arr] of byInstrument) {
    const sorted = [...arr].sort((a, b) => a.ts.getTime() - b.ts.getTime());
    let prev: ReadingLevel = 'NORMAL';
    for (const r of sorted) {
      const lvl = r.level ?? 'NORMAL';
      if (lvl !== 'NORMAL' && lvl !== prev) {
        events.push({
          id: `rd-${r.id}`,
          ts: r.ts,
          type: lvl, // ATENCAO | ALERTA | EMERGENCIA ⊂ EventType
          msg: `${code} atingiu nível ${EVENT_LABEL[lvl]} (${r.value})`,
          inst: code,
          tarp: null,
        });
      }
      prev = lvl;
    }
  }
  return events;
}

export interface HandoverLike {
  id: string;
  completedAt: Date;
  /** NULL = abertura de plantão, sem passagem (GUPM-272). */
  operatorOutName: string | null;
  operatorInId: string;
  operatorInName: string;
  /** `null` = plantão global, cobre as três barragens (GUPM-273). */
  damCode: string | null;
  /** Preenchidos quando o plantão foi encerrado sem passagem (GUPM-272 Rev.2). */
  endedAt: Date | null;
  endedById: string | null;
  endedByName: string | null;
  endReason: string | null;
  /**
   * Como o turno acabou (GUPM-273). `rendicao` não vira evento próprio — a
   * passagem já é contada na linha seguinte. `null` = linha anterior ao card,
   * cujo tipo se deriva de `endedById`.
   */
  endKind: 'rendicao' | 'solo' | 'admin' | null;
}

/**
 * Cada linha de plantão pode virar DOIS eventos de SISTEMA: um quando o turno
 * começa (abertura ou passagem) e outro quando termina sem rendição.
 *
 * O encerramento precisou de evento próprio (Rev.2): a versão anterior emitia um
 * evento por linha CRIADA, e encerrar é UPDATE na linha existente — então largar
 * a vigilância não deixava rastro nenhum no feed. O dado estava no `audit_log`
 * ("quem mexeu no quê"), mas não no log operacional ("o que aconteceu"), que é
 * onde se procura.
 *
 * As três frases são distintas de propósito:
 *   - abertura: interpolar `operatorOutName` nulo no texto de troca sairia como
 *     "null → Fulano" e apagaria o que mais importa — a operação ficou sem
 *     plantonista e alguém assumiu sozinho;
 *   - auto-encerramento: quem sai é o próprio plantonista;
 *   - destrave: um terceiro interrompeu o turno de outra pessoa. Confundir os
 *     dois últimos num texto só apagaria a diferença entre um turno encerrado e
 *     um turno interrompido pela supervisão.
 */
export function buildHandoverEvents(handovers: HandoverLike[]): LogEvent[] {
  const events: LogEvent[] = [];
  for (const h of handovers) {
    // GUPM-273: o plantão é global. Só a linha histórica carrega barragem, e
    // nela o rótulo continua aparecendo — some do texto em vez de virar "(null)".
    const escopo = h.damCode ? ` (${h.damCode})` : '';
    events.push({
      id: `ho-${h.id}`,
      ts: h.completedAt,
      type: 'SISTEMA',
      msg:
        h.operatorOutName === null
          ? `Abertura de plantão${escopo}: ${h.operatorInName} assumiu sem passagem`
          : `Troca de plantão${escopo}: ${h.operatorOutName} → ${h.operatorInName}`,
      inst: null,
      tarp: null,
    });

    // GUPM-273: desde "1 linha = 1 turno", a troca FECHA a linha anterior. Sem
    // este filtro, toda passagem geraria também um evento de encerramento — e
    // pelo critério antigo (ended_by = operator_in_id) ele sairia como "encerrou
    // o próprio plantão SEM RENDIÇÃO", que é o oposto do que houve. A rendição
    // já está contada no evento de troca da linha seguinte; contá-la de novo
    // aqui seria dizer duas coisas incompatíveis sobre o mesmo fato.
    if (h.endedAt && h.endKind !== 'rendicao') {
      // Comparação por ID, não por nome: dois homônimos na escala fariam um
      // destrave ser lido como "encerrou o próprio plantão". `endKind` é a fonte
      // quando existe; o ID cobre a linha histórica anterior ao card.
      const auto =
        h.endKind === 'solo' ||
        (h.endKind === null && h.endedById !== null && h.endedById === h.operatorInId);
      const quem = h.endedByName ?? 'usuário removido';
      events.push({
        id: `ho-end-${h.id}`,
        ts: h.endedAt,
        type: 'SISTEMA',
        msg: auto
          ? `Encerramento de plantão${escopo}: ${quem} encerrou o próprio plantão sem rendição`
          : `Encerramento de plantão${escopo}: ${quem} encerrou o plantão de ${h.operatorInName}`,
        inst: null,
        tarp: null,
      });
    }
  }
  return events;
}

/** Junta grupos de eventos e ordena por timestamp (mais recentes primeiro). */
export function mergeEvents(...groups: LogEvent[][]): LogEvent[] {
  return groups.flat().sort((a, b) => b.ts.getTime() - a.ts.getTime());
}

/** Filtro por tipo; `TODOS` devolve tudo. */
export function filterEvents(events: LogEvent[], filter: EventFilter): LogEvent[] {
  if (filter === 'TODOS') return events;
  return events.filter((e) => e.type === filter);
}

/**
 * Célula CSV segura: neutraliza formula injection do Excel/Sheets (prefixa `'`
 * quando o valor começa com `= + - @` ou tab/CR), escapa aspas e envolve em aspas.
 */
function csvCell(value: string): string {
  const v = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return `"${v.replace(/"/g, '""')}"`;
}

/** Serializa o feed em CSV (paridade com o export do protótipo). */
export function eventsToCsv(events: LogEvent[]): string {
  const header = 'Timestamp,Tipo,Mensagem,Instrumento,TARP';
  // ts (ISO) e type (enum) são controlados; msg/inst/tarp podem conter texto livre.
  const rows = events.map((e) =>
    [e.ts.toISOString(), e.type, csvCell(e.msg), csvCell(e.inst ?? ''), csvCell(e.tarp ?? '')].join(','),
  );
  return [header, ...rows].join('\n');
}
